import {TrendingUp,TrendingDown,Minus} from 'lucide-react';
import {compareMetric,metricIdentity} from '@cap4/cap4publish-domain';
import type {Analytics,Content,Metric} from '../../lib/types';
import {metricValue} from '../analytics/AnalyticsPage';
import {sourceNames} from '../analytics/analyticsState';
import {articleSummary} from '../analytics/articleSummary';
import {articleLocaleOrder} from '../analytics/articleLocaleOrder';

const labels:Record<string,string>={page_views:'Sidevisninger',views:'Visninger',article_views:'Artikelvisninger',clicks:'Klik',impressions:'Visninger',reach:'Rækkevidde',unique_reach:'Unik rækkevidde',engagements:'Interaktioner',reactions:'Reaktioner',likes:'Synes godt om',comments:'Kommentarer',shares:'Delinger',qualified_reading_sessions:'Kvalificerede læsninger',qualified_sessions:'Kvalificerede læsninger',average_active_reading_seconds:'Gns. aktiv læsetid',active_reading_seconds:'Aktiv læsetid',unique_readers:'Unikke læsere',sessions:'Sessioner',total_users:'Brugere',ctr:'Klikrate',completion_rate:'Læsegrad'};

function overviewMetrics(metrics:Metric[]){
 const priority=['page_views','views','clicks','qualified_reading_sessions','qualified_sessions','impressions','reach','engagements','reactions','article_views','average_active_reading_seconds'];
 const rank=(metric:Metric)=>priority.includes(metric.key)?priority.indexOf(metric.key):priority.length;
 const measured=metrics.filter(metric=>metric.value!==null).sort((a,b)=>rank(a)-rank(b));
 const selected:Metric[]=[],sources=new Set<string>();
 for(const metric of measured)if(!sources.has(metric.source)&&selected.length<3){selected.push(metric);sources.add(metric.source);}
 for(const metric of measured)if(selected.length<3&&!selected.includes(metric))selected.push(metric);
 return selected;
}

function missingLabel(metrics:Metric[]){
 if(metrics.some(metric=>metric.state==='needs_access'))return 'Måledata kræver adgang';
 if(metrics.some(metric=>metric.state==='error'))return 'Måledata kunne ikke opdateres';
 return 'Ingen måledata i perioden';
}

export function ArticleMetrics({item,data,loading,error}:{item:Content;data?:Analytics;loading:boolean;error:string;compare?:boolean}){
 const ids=item.kind==='social'?[item.publications?.[0]?.id||item.id]:(item.articleIds||[item.articleId||item.id]);
 const groups=data?.groups.filter(group=>ids.includes(item.kind==='social'?(group.publicationId||group.key):(group.itemId||group.key))).sort(articleLocaleOrder)||[];
 if(item.kind==='social'&&!groups.some(group=>group.metrics.some(metric=>metric.value!==null&&['available','partial'].includes(metric.state))))return null;
 return <div className="knowledge-card-metrics" aria-label="Resultater i den valgte periode">
  {loading?<p>Henter tal…</p>:error?<p role="status">Resultaterne kunne ikke opdateres.</p>:groups.length?groups.map(group=>{
   const metrics=overviewMetrics(group.metrics),summary=articleSummary(group.metrics);
   return <div className="content-metric-locale" key={group.key}>
    {group.locale&&<p className="content-metric-language">{group.locale.toUpperCase()}</p>}
    {item.kind!=='social'?<dl style={{gridTemplateColumns:`repeat(${summary.length}, minmax(0, 1fr))`}}>{summary.map(entry=><div key={entry.key}><dt>{entry.label}</dt><dd>{entry.metric?metricValue(entry.metric):'—'}</dd></div>)}</dl>:metrics.length?<dl style={{gridTemplateColumns:`repeat(${metrics.length}, minmax(0, 1fr))`}}>{metrics.map(metric=>{
     return <div key={metricIdentity(metric)}>
      <dt>{labels[metric.key]||'Måling'}</dt><dd>{metricValue(metric)}</dd>
      <small>{sourceNames[metric.source]||metric.source}{metric.basis==='lifetime'?' · Levetid':''}{metric.state==='partial'?' · Delvise data':''}</small>
     </div>;
    })}</dl>:<p>{missingLabel(group.metrics)}</p>}
   </div>;
  }):<p>Ingen måledata i perioden</p>}
 </div>;
}

export function WeeklyArticleMetrics({item,data,loading,error}:{item:Content;data?:Analytics;loading:boolean;error:string}){
 if(loading||error)return null;
 const ids=item.articleIds||[item.articleId||item.id];
 const groups=data?.groups.filter(group=>ids.includes(group.itemId||group.key)).sort(articleLocaleOrder)||[];
 const rows=groups.flatMap(group=>{
   const selected=articleSummary(group.metrics)[0]?.metric||articleSummary(group.comparison||[])[0]?.metric;
   const metric=selected&&group.metrics.find(p=>metricIdentity(p)===metricIdentity(selected));
   const previous=selected&&group.comparison?.find(p=>metricIdentity(p)===metricIdentity(selected));
   if(!metric||!previous)return [];
   const delta=compareMetric(metric,previous);
   if(delta.state!=='comparable'&&delta.state!=='previous_zero')return [];
   const difference=delta.absolute!;
   const Icon=difference>0?TrendingUp:difference<0?TrendingDown:Minus;
   const change=delta.state==='comparable'?new Intl.NumberFormat('da-DK',{maximumFractionDigits:0,signDisplay:'exceptZero'}).format(delta.percent!)+' %':new Intl.NumberFormat('da-DK',{signDisplay:'exceptZero'}).format(difference)+' visninger';
   const detail=`Ugen før: ${metricValue(previous)} · Seneste 7 dage: ${metricValue(metric)}`;
   return [<span className="article-weekly-value" key={group.key} title={detail}>
    <span>{group.locale?.toUpperCase()}</span><Icon size={15} aria-hidden="true"/><strong>{change}</strong>
    <span className="sr-only">{detail}</span>
   </span>];
 });
 if(!rows.length)return null;
 return <div className="article-weekly" aria-label="Udvikling uge for uge">
  <span className="article-weekly-label">Seneste 7 dage · mod ugen før</span>
  {rows}
 </div>;
}
